import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import type { JSX as EmotionJSX } from "@emotion/react/jsx-runtime";
import { IgButton, IgIconButton, IgTypography } from "@intelligems/ig-theme";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  Grid,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { isDialogOpen, setDialog } from "@src/store/slices/global";

type PagesType = {
  title: string;
  asset: string;
  description: EmotionJSX.Element;
}[];

const PREFIX = "OffersMarketingDialog";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "unset",
  },
}));

export const OffersMarketingDialog = () => {
  const theme = useTheme();

  const pages = [
    {
      title: "Build offers that motivate",
      asset: "/lottie/offers_screen_1.lottie",
      description: (
        <IgTypography
          variant={"h3"}
          sx={{ color: theme.palette.text.secondary }}
        >
          Create promotions that drive behavior and boost AOV. From volume
          discounts to free shipping thresholds, build it all seamlessly and
          without leaving Intelligems.
        </IgTypography>
      ),
    },
    {
      title: "Ready-made components",
      asset: "/lottie/offers_screen_2.lottie",
      description: (
        <IgTypography
          variant={"h3"}
          sx={{ color: theme.palette.text.secondary }}
        >
          An offer is only as effective as the experience behind it. Add
          quantity buttons, progress bars, or offer messages. All ready to drop
          into your site and styled to match your brand.
        </IgTypography>
      ),
    },
    {
      title: "Measure with confidence",
      asset: "/lottie/offers_screen_3.lottie",
      description: (
        <IgTypography
          variant={"h3"}
          sx={{ color: theme.palette.text.secondary }}
        >
          Test your offers or deploy them to specific segments, and know exactly
          what’s working. Discover which experiences drive the most profit
          across customer groups and channels.
        </IgTypography>
      ),
    },
  ];

  const pageCount = pages.length;
  const [page, setPage] = useImmer(0);
  const pageData = pages[page];
  const showUserInfoPopup = useSelector(isDialogOpen("offersMarketingDialog"));
  const isOnboardingOpen = useSelector(isDialogOpen("onboarding"));
  const dispatch = useDispatch();
  const nextPage = () => setPage((currPage) => currPage + 1);
  const backPage = () => setPage((currPage) => currPage - 1);

  const handleClose = () => {
    dispatch(setDialog({ dialog: "offersMarketingDialog", open: false }));
  };

  if (isOnboardingOpen) {
    return null;
  }

  return (
    <StyledDialog
      maxWidth="md"
      open={showUserInfoPopup}
      aria-labelledby="user-info-popup"
      aria-describedby="user-info-popup"
      onClose={handleClose}
      PaperProps={{
        style: {
          borderRadius: "6px",
          width: "850px",
          minWidth: "850px",
        },
      }}
    >
      <Card elevation={0} sx={{ textAlign: "center", borderRadius: "6px" }}>
        <CardMedia
          sx={{
            height: "400px",
            overflow: "hidden !important",
            backgroundSize: page === 0 ? "contain" : undefined,
            borderRadius: "6px",
          }}
          title="index"
          image={page === 0 ? pageData.asset : undefined}
        >
          <Grid
            container
            direction={"row"}
            justifyContent={"flex-end"}
            sx={{ position: "absolute", marginTop: 2, right: 6 }}
          >
            <Grid item>
              <IgIconButton
                onClick={() => {
                  handleClose();
                }}
              >
                <X size={24} />
              </IgIconButton>
            </Grid>
          </Grid>

          <DotLottieReact
            src={pageData.asset}
            loop={true}
            autoplay={true}
            renderConfig={{
              autoResize: true,
            }}
            layout={{
              fit: "cover",
            }}
          />
        </CardMedia>
        <CardContent
          sx={{ height: "253px", padding: "0px !important", zIndex: 10000 }}
        >
          <Grid
            item
            sx={{
              paddingRight: "40px",
              paddingLeft: "40px",
              paddingTop: "40px",
              paddingBottom: "30px",
            }}
            container
            direction={"column"}
          >
            <PageContent
              page={page}
              pageCount={pageCount}
              pageData={pageData}
              handleClose={handleClose}
              backPage={backPage}
              nextPage={nextPage}
            />
          </Grid>
        </CardContent>
      </Card>
    </StyledDialog>
  );
};

interface PageContentProps {
  page: number;
  pageCount: number;
  pageData: PagesType[number];
  handleClose: () => void;
  nextPage: () => void;
  backPage: () => void;
}

const PageContent = ({
  pageData,
  pageCount,
  page,
  handleClose,
  nextPage,
  backPage,
}: PageContentProps) => {
  const lastPage = pageCount - 1;

  const theme = useTheme();
  return (
    <>
      <Grid
        item
        container
        direction={"row"}
        justifyContent={"space-between"}
        textAlign={"start"}
        rowSpacing={1}
        sx={{
          height: "150px",
        }}
      >
        <Grid item xs={5} sx={{ paddingTop: "0px !important" }}>
          <IgTypography variant={"display"} igStyle={"semibold"}>
            {pageData.title}
          </IgTypography>
        </Grid>
        <Grid
          item
          xs={7}
          sx={{ paddingTop: "0px !important", maxWidth: "96px", width: "96px" }}
        >
          {pageData.description}
        </Grid>
      </Grid>
      <Grid item container direction={"row"} wrap={"nowrap"} alignItems={"end"}>
        {page !== 0 && (
          <Grid item>
            <IgButton onClick={backPage} color={"secondary"}>
              Back
            </IgButton>
          </Grid>
        )}
        <Grid item container direction={"row"} justifyContent={"center"}>
          {new Array(pageCount).fill(null).map((_, idx) => (
            <Box
              key={idx}
              sx={{
                backgroundColor:
                  page === idx
                    ? theme.igBackgroundColors.interactive.dark.default
                    : theme.igBackgroundColors.interactive.dark.disabled,
                height: "6px",
                width: page === idx ? "17px" : "6px",
                borderRadius: "71px",
                marginLeft: idx > 0 ? "6px" : undefined,
              }}
            />
          ))}
        </Grid>
        <Grid item>
          <IgButton
            onClick={() => {
              if (page === lastPage) {
                handleClose();
              } else {
                nextPage();
              }
            }}
            variant={page === lastPage ? "outlined" : "contained"}
          >
            {page === lastPage ? "Close" : "Next"}
          </IgButton>
        </Grid>
      </Grid>
    </>
  );
};
